import React from "react";
import PaymentPage from "../pages/PaymentPage";
import "./Result.css";

// Component to display an alert message
const AlertMessage = ({ type, message }) => (
  <div className={`alert alert-${type} mt-4 text-center fw-bold p-3 rounded shadow-sm`} style={{ maxWidth: "900px" }}>
    <strong>{message}</strong>
  </div>
);

// Component to display emissions for a single flight
const FlightEmissions = ({ flight, origin, destination, passengers, cabinClass, tripType }) => {
  const emissions = flight.emissionsGramsPerPax;

  // Check if emissions data is available
  if (!emissions || Object.keys(emissions).length === 0) {
    return tripType === "One way" ? <AlertMessage type="warning" message={"Emissions Data Not Found"} />: null;
  }

  // Determine which cabin classes to show
  const cabinClassesToShow =
    cabinClass.toLowerCase() === "all"
      ? emissions // Show all cabin classes
      : { [cabinClass]: emissions[cabinClass] }; // Show only the selected cabin class

  return (
    <div className="mb-4">
      <h5 className="text-secondary fw-bold">
        Flight: {origin} → {destination}
      </h5>
      {Object.entries(cabinClassesToShow).map(([classType, gramsPerPax]) => {
        const emissionsPerPassenger = 1 + (gramsPerPax / 1000) / 1000; // Convert grams to tons
        const totalEmissions = emissionsPerPassenger * passengers;

        return (
          <p key={classType}>
            <strong>{classType.charAt(0).toUpperCase() + classType.slice(1)} Class Emissions:</strong>{" "}
            {totalEmissions.toFixed(2)} t
          </p>
        );
      })}
      {passengers > 1 && (
        <p className="text-muted">
          <small>Note: Calculated for {passengers} passengers.</small>
        </p>
      )}
      <hr />
    </div>
  );
};

const CustomFlightEmissions = ({ origin, destination, passengers, cabinClass, tripType }) => {
  return (
    <>
    <div className="mb-4">
      <h5 className="text-secondary fw-bold">
        Flight: {origin} → {destination}
      </h5>    
      <p>
        <strong>{cabinClass.charAt(0).toUpperCase() + cabinClass.slice(1)} Class Emissions:</strong>
      </p>
      {passengers > 1 && (
        <p className="text-muted">
          <small>Note: Calculated for {passengers} passengers.</small>
        </p>
      )}
      <hr />
    </div>
  </>
  );
};

const PaidDetails = ({ amount, paymentId }) => {  
  return (
    <div className="mb-4">
      <p className="text-secondary fw-bold">
        <strong>Calculated Amount: { amount ? `$ ${amount.toFixed(2)}` : `From $20 upwards`} </strong>
      </p>     
      {paymentId?.status === "Paid" && (
        <p className="text-success">
          <strong>Paid Amount: $ {paymentId?.paidAmount.toFixed(2)}</strong>
        </p>
      )}
      <hr />
    </div>
  );
};

// Calculate the amount based on cabin class, passenger count, and trip type
const calculateAmount = (emissionsGramsPerPax, cabinClass, passengers, tripType) => {
  if (!emissionsGramsPerPax) return 0;

  // Use economy class emissions if "all" is selected, otherwise use the selected cabin class
  const selectedEmissions =
    cabinClass.toLowerCase() === "all"
      ? emissionsGramsPerPax.economy // Default to economy class
      : emissionsGramsPerPax[cabinClass];

  let emissionsInTons =  1 + (( selectedEmissions / 1000) / 1000); // Convert grams to tons

  // Multiply emissions per passenger by the number of passengers
  let totalEmissions = emissionsInTons * passengers;

  // Double the emissions for round trips
  if (tripType.toLowerCase() === "round trip") {
    totalEmissions *= 2;
  }

  // Convert grams to kilograms and return the amount as a whole number
  return Math.round(totalEmissions * 20 ); // Amount in Tones (rounded to the nearest whole number)
};

// Main Result component
function Result({ data, error }) {
  if (error) {
    return <AlertMessage type="danger" message={`Error: ${error}`} />;
  }

  if (!data) {
    return null;
  }

  const { request, flightDetails, googleApiResponse, paymentId, emissionId } = data;
  const { origin, destination, passengers, cabinClass, tripType = "One Way", email } = request;

  const operatingCarrierCode = flightDetails?.operatingCarrierCode || "Unknown";
  const emissionsGramsPerPax = googleApiResponse?.flightEmissions[0]?.emissionsGramsPerPax;
  const amount = calculateAmount(emissionsGramsPerPax, cabinClass, passengers, tripType);
  return (
    <div className="container mt-4 p-4 shadow-lg bg-light rounded position-relative" style={{ maxWidth: "900px" }}>
      {/* Paid Badge */}
      {paymentId?.status === "Paid" && (
        <div className="paid-badge">
          <span className="badge bg-success fs-6">Paid</span>
        </div>
      )}

      <h4 className="text-primary fw-bold text-center">Flight Emissions Details</h4>
      <p className="text-center text-muted">
        Showing emissions for: <strong>{origin}</strong> → <strong>{destination}</strong>
        {tripType === "Round Trip" && (
          <>
            {" "}
            | <strong>{destination}</strong> → <strong>{origin}</strong>
          </>
        )}
      </p>
      <hr />

      {!emissionsGramsPerPax ? 
          // <AlertMessage type="danger" message="The flight data is not available now. Pls try again after 1 hour." />
          <>
            {paymentId?.status === "Paid" ? 
            <>      
            <CustomFlightEmissions origin={origin} destination={destination} cabinClass={cabinClass} passengers={passengers} tripType={tripType}/>  
            <PaidDetails amount={0} paymentId={paymentId} />
            </> :
              <div className="text-center">
                  <AlertMessage type="warning" message="No direct flight found in our records. If there are stopovers, calculate each leg separately. Otherwise, check the estimated amount below and pay as you prefer." />
                  <PaymentPage amount={0} emissionId={emissionId} email={email} />
                  <hr />
              </div>  
            } 
          </>          
       : (
        <>
          {paymentId?.status === "Paid" ? 
          <>
            {googleApiResponse.flightEmissions.map((flight, index) => (
            <FlightEmissions
              key={index}
              flight={flight}
              origin={origin}
              destination={destination}
              passengers={passengers}
              cabinClass={cabinClass}
              tripType={"One way"}
            />
          ))}

          {tripType === "Round Trip" &&
            googleApiResponse.flightEmissions.map((flight, index) => (
              <FlightEmissions
                key={`return-${index}`}
                flight={flight}
                origin={destination}
                destination={origin}
                passengers={passengers}
                cabinClass={cabinClass}
                tripType={"Round Trip"}
              />
            ))}
            <PaidDetails amount={amount} paymentId={paymentId} />
          </>:          
            <div className="text-center">
              <PaymentPage amount={amount} emissionId={emissionId} email={email} />
               <hr />
            </div>
          }
        </>
      )}

      {/* Model Version Information */}
      {googleApiResponse?.modelVersion && (
        <div className="text-center mt-4">
        <p className="text-muted text-center mt-4">
          <small>
            <strong>Model Version:</strong> {googleApiResponse.modelVersion.major}.
            {googleApiResponse.modelVersion.minor}.{googleApiResponse.modelVersion.patch} (Dated:{" "}
            {googleApiResponse.modelVersion.dated})
          </small>
        </p>
        </div>
      )}
    </div>
  );
}

export default Result;